import React, { useState, useEffect, useMemo } from 'react';
import Header from './Header';
import SEO from './SEO';
import wordList from '../data/5-letter-words.json';
import { RefreshCw, Trash2, Check, HelpCircle, XCircle, Sparkles } from 'lucide-react';

import Sidebar from './Sidebar';

const DailyGuessSolverPage = () => {
  // State for inputs
  const [correctLetters, setCorrectLetters] = useState(['', '', '', '', '']); // Green
  const [misplacedLetters, setMisplacedLetters] = useState(['', '', '', '', '']); // Yellow
  const [excludedLetters, setExcludedLetters] = useState(''); // Grey
  
  // Computed state
  const [filteredWords, setFilteredWords] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Initialize word list
  useEffect(() => {
    // Check if wordList is array, if not handle potential issues
    if (Array.isArray(wordList)) {
      setFilteredWords(wordList);
      setIsLoaded(true);
    }
  }, []);

  // Filter Logic
  useEffect(() => {
    if (!isLoaded) return;

    const lowerExcluded = excludedLetters.toLowerCase();
    
    // Create a set of "must have" letters from yellow inputs
    // We handle yellow inputs as: letter at index I means "contains this letter, but NOT at index I"
    const mustHaveLetters = new Set();
    misplacedLetters.forEach(letters => {
      for (const char of letters) {
        if (char) mustHaveLetters.add(char.toLowerCase());
      }
    });

    const results = wordList.filter(word => {
      const lowerWord = word.toLowerCase();

      // 1. Check Green (Correct Position)
      for (let i = 0; i < 5; i++) {
        if (correctLetters[i] && lowerWord[i] !== correctLetters[i].toLowerCase()) {
          return false;
        }
      }

      // 2. Check Gray (Excluded)
      // Standard rule: If a letter is excluded, it shouldn't appear in the word.
      // Edge case handling: If a user marks 'E' as green/yellow AND gray, strictly speaking it's a contradiction 
      // in simple mode, but in Wordle 'Gray' usually means "no MORE instances of this letter".
      // For this simple solver, we will strictly exclude any word containing a gray letter
      // UNLESS that letter is already established as required (Green/Yellow).
      // This prevents "E is Green at 1" and "E is Gray" (from a second guess) making the list empty.
      
      for (let char of lowerExcluded) {
         // If this excluded char is NOT in our "must have" or "green" list, we reject the word if it has it.
         // If it IS in our "must have" list, we only reject if the word has MORE of them than we know about?
         // To keep it simple and intuitive: If user puts it in Gray, they usually mean "remove it".
         // But if they also put it in Green, they made a mistake or are confused. 
         // Let's stick to strict exclusion to guide them to remove it from Gray if they found it.
         if (lowerWord.includes(char)) {
            // Check if this char is actually required elsewhere. If so, don't exclude solely based on this.
            // But usually, Gray means "this specific instance is wrong" or "no more of this".
            // Let's do simple strict exclusion.
            return false;
         }
      }

      // 3. Check Yellow (Misplaced)
      // Rule A: Word must contain the letter.
      // Rule B: Word must NOT have the letter at this specific index.
      for (let i = 0; i < 5; i++) {
        const lettersAtPos = misplacedLetters[i].toLowerCase();
        for (const char of lettersAtPos) {
           if (char) {
             // Rule B: Not at this spot
             if (lowerWord[i] === char) return false;
             // Rule A: Must be in word (we verified this with `mustHaveLetters` check implicitly? No, need to check count or just presence)
           }
        }
      }

      // Final check: Does the word contain ALL "yellow" letters somewhere?
      for (let char of mustHaveLetters) {
        if (!lowerWord.includes(char)) return false;
      }

      return true;
    });

    setFilteredWords(results);
  }, [correctLetters, misplacedLetters, excludedLetters, isLoaded]);

  // Handlers
  const handleGreenChange = (index, val) => {
    const newVal = val.slice(-1).toUpperCase(); // Only last char, uppercase
    const newArr = [...correctLetters];
    newArr[index] = newVal;
    setCorrectLetters(newArr);
    
    // Auto-focus next input if value added
    if (newVal && index < 4) {
      document.getElementById(`green-${index + 1}`)?.focus();
    }
  };

  const handleYellowChange = (index, val) => {
    // Allow multiple chars for yellow (e.g. user tried 'A' and 'B' at this spot and both yellow)
    // Actually, usually yellow means "I tried 'A' here and it was yellow".
    // So usually 1 char per guess. But if solving multiple guesses, you might know 'A' and 'B' are not at pos 1.
    // Let's allow typing multiple chars.
    const newVal = val.toUpperCase();
    const newArr = [...misplacedLetters];
    newArr[index] = newVal;
    setMisplacedLetters(newArr);
  };

  const resetAll = () => {
    setCorrectLetters(['', '', '', '', '']);
    setMisplacedLetters(['', '', '', '', '']);
    setExcludedLetters('');
  };

  // Schema for SEO
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "DailyGuess Solver",
    "applicationCategory": "Game",
    "operatingSystem": "Any",
    "description": "Free tool to solve DailyGuess and Wordle puzzles. Enter correct, misplaced, and excluded letters to find the answer.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#f8f9fa] bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:24px_24px]">
      <SEO 
        title="DailyGuess Solver - Free Wordle & Daily Guess Helper"
        description="Solve DailyGuess and Wordle puzzles instantly. Enter your green, yellow, and gray tiles to see all possible 5-letter words remaining."
        url="https://pockethints.com/daily-guess-solver" // Replace with actual domain logic if needed
        schema={schema}
      />
      <Header 
        date={new Date().toISOString()} 
        title="DailyGuess Solver" 
        gameName="DailyGuess Helper" 
        heroTitle={
          <span className="text-gray-900">
            Master the <span className="text-brand-blue relative inline-block">
              DailyGuess
              <svg className="absolute w-full h-3 -bottom-1 left-0 text-brand-yellow -z-10 opacity-60" viewBox="0 0 100 10" preserveAspectRatio="none">
                 <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
              </svg>
            </span>
            <br />
            Crack the Code Instantly
          </span>
        }
      />
      
      <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 w-full flex flex-col lg:flex-row gap-8 py-8">
        <div className="flex-1 min-w-0">
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">DailyGuess Solver</h1>
              <p className="text-gray-600">Enter the feedback from your game to find the hidden word.</p>
            </div>
            <button 
              onClick={resetAll}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
            >
              <Trash2 size={16} />
              Reset
            </button>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-1">
            
            {/* INPUT SECTION */}
            <div className="space-y-6">
              
              {/* GREEN */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                  <span className="w-4 h-4 rounded-full bg-green-500 inline-block"></span>
                  Correct Position (Green)
                </label>
                <div className="flex gap-2">
                  {correctLetters.map((val, i) => (
                    <input
                      key={`green-${i}`}
                      id={`green-${i}`}
                      type="text"
                      maxLength={1}
                      value={val}
                      onChange={(e) => handleGreenChange(i, e.target.value)}
                      className="w-12 h-14 md:w-14 md:h-16 text-center text-2xl font-bold border-2 border-gray-300 rounded-md focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none uppercase bg-green-50"
                      placeholder=""
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2">Enter letters that are in the correct spot.</p>
              </div>

              {/* YELLOW */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                  <span className="w-4 h-4 rounded-full bg-yellow-400 inline-block"></span>
                  Misplaced (Yellow)
                </label>
                <div className="flex gap-2">
                  {misplacedLetters.map((val, i) => (
                    <input
                      key={`yellow-${i}`}
                      type="text"
                      value={val}
                      onChange={(e) => handleYellowChange(i, e.target.value)}
                      className="w-12 h-14 md:w-14 md:h-16 text-center text-xl font-bold border-2 border-gray-300 rounded-md focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200 outline-none uppercase bg-yellow-50 placeholder-gray-300"
                      placeholder=""
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2">Enter letters that are in the word but NOT at this spot. You can type multiple letters per box.</p>
              </div>

              {/* GRAY */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                  <span className="w-4 h-4 rounded-full bg-gray-500 inline-block"></span>
                  Excluded (Gray)
                </label>
                <input
                  type="text"
                  value={excludedLetters}
                  onChange={(e) => setExcludedLetters(e.target.value.toUpperCase())}
                  className="w-full p-3 text-lg font-medium border-2 border-gray-300 rounded-md focus:border-gray-500 focus:ring-2 focus:ring-gray-200 outline-none uppercase tracking-widest bg-gray-50"
                  placeholder="XYZ..."
                />
                <p className="text-xs text-gray-400 mt-2">Enter letters that are not in the word at all.</p>
              </div>

            </div>

            {/* RESULTS SECTION */}
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 h-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Check size={20} className="text-green-600" />
                  Possible Words
                </h2>
                <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2.5 py-0.5 rounded-full">
                  {filteredWords.length} found
                </span>
              </div>
              
              <div className="h-96 overflow-y-auto pr-2 custom-scrollbar">
                {filteredWords.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {filteredWords.slice(0, 100).map((word, idx) => (
                      <div key={word + idx} className="bg-white px-3 py-2 rounded shadow-sm text-center font-mono text-lg text-gray-700 tracking-wider border border-gray-100">
                        {word.toUpperCase()}
                      </div>
                    ))}
                    {filteredWords.length > 100 && (
                      <div className="col-span-full text-center py-2 text-gray-500 text-sm italic">
                        ...and {filteredWords.length - 100} more
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <XCircle size={48} className="mb-2 opacity-20" />
                    <p>No words match your criteria.</p>
                    <button onClick={resetAll} className="text-blue-500 text-sm hover:underline mt-2">Try resetting</button>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Content for SEO */}
        <div className="prose max-w-none text-gray-600">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use the DailyGuess Solver</h2>
          <p className="mb-4">
            DailyGuess is a popular word puzzle game similar to Wordle where players can create and share their own 5-letter word puzzles. 
            Our solver helps you find the solution when you're stuck.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <h3 className="font-bold text-lg mb-2 text-green-600">1. Green Tiles</h3>
              <p className="text-sm">Enter letters that are in the correct position in the "Correct Position" row. These are the letters that turn green in your game.</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <h3 className="font-bold text-lg mb-2 text-yellow-500">2. Yellow Tiles</h3>
              <p className="text-sm">Enter letters that are in the word but in the wrong spot in the "Misplaced" row. Make sure to put them in the column corresponding to where they appeared yellow.</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <h3 className="font-bold text-lg mb-2 text-gray-500">3. Gray Tiles</h3>
              <p className="text-sm">Type all the letters that have been excluded (turned gray or dark) in the bottom box to remove them from potential answers.</p>
            </div>
          </div>

          
          <h2 className="text-xl font-bold text-gray-800 mb-3">Tips for Solving Word Puzzles</h2>
          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li><strong>Start with vowels:</strong> Words like "ADIEU" or "AUDIO" are great starting words because they eliminate or confirm multiple vowels at once.</li>
            <li><strong>Use common consonants:</strong> Letters like R, S, T, L, and N appear frequently in 5-letter words.</li>
            <li><strong>Don't repeat gray letters:</strong> Once a letter is gray, avoid using it in future guesses unless you suspect it appears twice (though typically gray means it's not there at all).</li>
            <li><strong>Look for patterns:</strong> Common endings like -ER, -ED, -ING (rare for 5 letters), or -TH can help narrow down the list.</li>
          </ul>
        </div>
        </div>

        {/* Sidebar (Desktop Only) */}
        <div className="hidden lg:block w-80 flex-shrink-0">
          <div>
            <Sidebar />
          </div>
        </div>
      </main>
    </div>
  );
};

export default DailyGuessSolverPage;

